import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import { ThemeProvider } from '@/contexts/ThemeContext'

// Layout
import MainLayout from '@/components/layout/MainLayout'
import AuthLayout from '@/components/layout/AuthLayout'

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import Setup2FAPage from '@/pages/auth/Setup2FAPage'
import PendingApprovalPage from '@/pages/auth/PendingApprovalPage'

// Main Pages
import DashboardPage from '@/pages/DashboardPage'
import ProfilePage from '@/pages/ProfilePage'
import NotificationsPage from '@/pages/NotificationsPage'

// Forum Pages
import ForumListPage from '@/pages/forum/ForumListPage'
import ForumDetailPage from '@/pages/forum/ForumDetailPage'
import ThreadDetailPage from '@/pages/forum/ThreadDetailPage'
import NewThreadPage from '@/pages/forum/NewThreadPage'

// LMS Pages
import CoursesPage from '@/pages/lms/CoursesPage'
import MyCoursesPage from '@/pages/lms/MyCoursesPage'
import CourseDetailPage from '@/pages/lms/CourseDetailPage'
import LessonPage from '@/pages/lms/LessonPage'
import QuizPage from '@/pages/lms/QuizPage'
import MyCertificatesPage from '@/pages/lms/MyCertificatesPage'

// Documents Pages
import DocumentsPage from '@/pages/documents/DocumentsPage'

// Events Pages
import EventsPage from '@/pages/events/EventsPage'
import EventDetailPage from '@/pages/events/EventDetailPage'

// Task Force Pages
import TaskForcesPage from '@/pages/taskforce/TaskForcesPage'
import TaskForceDetailPage from '@/pages/taskforce/TaskForceDetailPage'
import MyTaskForcesPage from '@/pages/taskforce/MyTaskForcesPage'

// Services Pages
import ClinicPage from '@/pages/services/ClinicPage'
import ServicesPage from '@/pages/services/ServicesPage'

// Gamification Pages
import LeaderboardPage from '@/pages/gamification/LeaderboardPage'
import BadgesPage from '@/pages/gamification/BadgesPage'
import EscapeRoomPage from '@/pages/gamification/EscapeRoomPage'
import TournamentPage from '@/pages/gamification/TournamentPage'

// Admin Pages
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage'
import AdminUsersPage from '@/pages/admin/AdminUsersPage'
import AdminForumPage from '@/pages/admin/AdminForumPage'
import AdminCoursesPage from '@/pages/admin/AdminCoursesPage'
import AdminCertificateTemplatesPage from '@/pages/admin/AdminCertificateTemplatesPage'
import AdminUsersImportPage from '@/pages/admin/AdminUsersImportPage'
import AdminCourseDetailPage from '@/pages/admin/AdminCourseDetailPage'
import AdminEventsPage from '@/pages/admin/AdminEventsPage'
import AdminDocumentsPage from '@/pages/admin/AdminDocumentsPage'
import AdminTaskForcePage from '@/pages/admin/AdminTaskForcePage'
import AdminClinicPage from '@/pages/admin/AdminClinicPage'
import AdminClinicRoutingPage from '@/pages/admin/AdminClinicRoutingPage'
import AdminServicesPage from '@/pages/admin/AdminServicesPage'
import AdminPermissionsPage from '@/pages/admin/AdminPermissionsPage'
import AdminGamificationPage from '@/pages/admin/AdminGamificationPage'
import TorneiAdminPage from '@/pages/admin/TorneiAdminPage'
import AdminNewsletterPage from '@/pages/admin/AdminNewsletterPage'
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage'

// Help Pages
import HelpPage from '@/pages/help/HelpPage'

// Not Found
import NotFoundPage from '@/pages/NotFoundPage'

// Protected Route Component - usa PERMESSI SINGOLI dal database
// I permessi sono DINAMICI: se l'admin li cambia, al prossimo login l'utente vede le modifiche
interface ProtectedRouteProps {
  children: React.ReactNode
  permission?: string        // Permesso SINGOLO richiesto
  superAdminOnly?: boolean   // Se true, solo super_admin può accedere
}

const ProtectedRoute = ({
  children,
  permission,
  superAdminOnly = false
}: ProtectedRouteProps) => {
  const { isAuthenticated, user } = useAuthStore()
  const { hasPermission, isSuperAdmin } = usePermissions()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role === 'pending') {
    return <Navigate to="/pending-approval" replace />
  }

  // Controllo super_admin only
  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  // Controllo permesso SINGOLO dal database
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <ThemeProvider>
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/setup-2fa" element={<Setup2FAPage />} />

        {/* Forum - richiede forum.view */}
        <Route path="/forum" element={<ProtectedRoute permission="forum.view"><ForumListPage /></ProtectedRoute>} />
        <Route path="/forum/new" element={<ProtectedRoute permission="forum.create_thread"><NewThreadPage /></ProtectedRoute>} />
        <Route path="/forum/thread/:threadId" element={<ProtectedRoute permission="forum.view"><ThreadDetailPage /></ProtectedRoute>} />
        <Route path="/forum/category/:slug" element={<ProtectedRoute permission="forum.view"><ForumDetailPage /></ProtectedRoute>} />
        <Route path="/forum/:slug/thread/:threadId" element={<ProtectedRoute permission="forum.view"><ThreadDetailPage /></ProtectedRoute>} />

        {/* LMS - richiede lms.view */}
        <Route path="/courses" element={<ProtectedRoute permission="lms.view"><CoursesPage /></ProtectedRoute>} />
        <Route path="/courses/my-courses" element={<ProtectedRoute permission="lms.view"><MyCoursesPage /></ProtectedRoute>} />
        <Route path="/courses/:slug" element={<ProtectedRoute permission="lms.view"><CourseDetailPage /></ProtectedRoute>} />
        <Route path="/courses/:slug/lesson/:lessonId" element={<ProtectedRoute permission="lms.view"><LessonPage /></ProtectedRoute>} />
        <Route path="/courses/:slug/lesson/:lessonId/quiz" element={<ProtectedRoute permission="lms.view"><QuizPage /></ProtectedRoute>} />
        <Route path="/my-certificates" element={<ProtectedRoute permission="lms.view"><MyCertificatesPage /></ProtectedRoute>} />

        {/* Documents - richiede documents.view */}
        <Route path="/documents" element={<ProtectedRoute permission="documents.view"><DocumentsPage /></ProtectedRoute>} />

        {/* Events - richiede events.view */}
        <Route path="/events" element={<ProtectedRoute permission="events.view"><EventsPage /></ProtectedRoute>} />
        <Route path="/events/:slug" element={<ProtectedRoute permission="events.view"><EventDetailPage /></ProtectedRoute>} />

        {/* Task Force - richiede taskforce.view */}
        <Route path="/taskforce" element={<ProtectedRoute permission="taskforce.view"><TaskForcesPage /></ProtectedRoute>} />
        <Route path="/taskforce/my" element={<ProtectedRoute permission="taskforce.view"><MyTaskForcesPage /></ProtectedRoute>} />
        <Route path="/taskforce/:slug" element={<ProtectedRoute permission="taskforce.view"><TaskForceDetailPage /></ProtectedRoute>} />

        {/* Services - richiede services.view */}
        <Route path="/services" element={<ProtectedRoute permission="services.view"><ServicesPage /></ProtectedRoute>} />
        <Route path="/clinic" element={<ProtectedRoute permission="services.view"><ClinicPage /></ProtectedRoute>} />

        {/* Gamification - richiede gamification.view */}
        <Route path="/leaderboard" element={<ProtectedRoute permission="gamification.view"><LeaderboardPage /></ProtectedRoute>} />
        <Route path="/badges" element={<ProtectedRoute permission="gamification.view"><BadgesPage /></ProtectedRoute>} />
        <Route path="/escape-room" element={<ProtectedRoute permission="gamification.view"><EscapeRoomPage /></ProtectedRoute>} />
        <Route path="/tournaments" element={<ProtectedRoute permission="gamification.view"><TournamentPage /></ProtectedRoute>} />
        <Route path="/tournaments/:slug" element={<ProtectedRoute permission="gamification.view"><TournamentPage /></ProtectedRoute>} />

        {/* Admin Routes - ogni route ha UN permesso SINGOLO dal database */}
        {/* Se l'admin cambia i permessi nel pannello, l'accesso cambia al prossimo login */}
        <Route
          path="/gestione"
          element={
            <ProtectedRoute permission="admin.access_panel">
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/users"
          element={
            <ProtectedRoute permission="admin.manage_users">
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/users/import"
          element={
            <ProtectedRoute permission="admin.manage_users">
              <AdminUsersImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/forum"
          element={
            <ProtectedRoute permission="forum.moderate">
              <AdminForumPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/courses"
          element={
            <ProtectedRoute permission="lms.create_course">
              <AdminCoursesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/attestati"
          element={
            <ProtectedRoute permission="lms.edit_course">
              <AdminCertificateTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/courses/:id"
          element={
            <ProtectedRoute permission="lms.edit_course">
              <AdminCourseDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/events"
          element={
            <ProtectedRoute permission="events.create">
              <AdminEventsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/tornei"
          element={
            <ProtectedRoute permission="admin.access_panel">
              <TorneiAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/documents"
          element={
            <ProtectedRoute permission="documents.approve">
              <AdminDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/taskforce"
          element={
            <ProtectedRoute permission="taskforce.manage_members">
              <AdminTaskForcePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/clinic"
          element={
            <ProtectedRoute permission="services.assign">
              <AdminClinicPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/clinic-routing"
          element={
            <ProtectedRoute permission="services.assign">
              <AdminClinicRoutingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/services"
          element={
            <ProtectedRoute permission="admin.access_panel">
              <AdminServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/permissions"
          element={
            <ProtectedRoute permission="admin.manage_roles">
              <AdminPermissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/gamification"
          element={
            <ProtectedRoute permission="gamification.manage_badges">
              <AdminGamificationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/newsletter"
          element={
            <ProtectedRoute permission="newsletter.create">
              <AdminNewsletterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestione/settings"
          element={
            <ProtectedRoute superAdminOnly>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Help Routes - accessible without MainLayout for cleaner UI */}
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help/:chapterId"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help/:chapterId/:sectionId"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </ThemeProvider>
  )
}

export default App
